import * as joint from "jointjs/dist/joint";
import DefalutLink from "./Link";
import SelectArea from "./SelectArea";
import { expressionCalculate } from "@/api";
import { message } from "ant-design-vue";
import * as R from "ramda";
import { getStencils } from "@/stencils/index";

const stencilsObj = (() => {
  const stencils = getStencils();
  const tmp = {};
  for (let groupKeyValue of Object.entries(stencils)) {
    for (let [stencilKey, stencilValue] of Object.entries(groupKeyValue[1])) {
      tmp[stencilKey] = stencilValue;
    }
  }

  return tmp;
})();

class Joint {
  constructor() {
    this.dom = null;
    this.graph = null;
    this.paper = null;

    this.selectedElements = [];
    this.selectedElementsChangeHookCount = 0;
    this.selectedElementsChangeHook = {};

    this.selectedArea = {};

    this.scaleTimes = 1;
  }

  init(dom, width, height) {
    this.dom = dom;
    const graph = new joint.dia.Graph();

    const paper = new joint.dia.Paper({
      el: dom,
      width: width,
      height: height,
      model: graph,
      gridSize: 5,
      drawGrid: {
        name: "doubleMesh",
        args: [
          { color: "rgba(42, 55, 71, 1)", scaleFactor: 4, thickness: 1 }, // settings for the primary mesh
          { color: "rgba(42, 55, 71, 1)", scaleFactor: 20, thickness: 1 }, //settings for the secondary mesh
        ],
      },
      background: {
        color: "rgba(255,255,255,0)",
      },
      linkPinning: false,
      snapLinks: { radius: 1 },
      defaultLink: new DefalutLink(),
      defaultConnectionPoint: { name: "boundary" },
      validateMagnet(cellView, magnet, evt) {
        const model = cellView.model;
        const result = model.validateMagnet(cellView, magnet, evt);
        return result;
      },
      validateConnection(
        cellViewS,
        magnetS,
        cellViewT,
        magnetT,
        end,
        linkView
      ) {
        let result = true;

        validate: {
          if (cellViewS.id === cellViewT.id) {
            // source cell 和 target cell 相同时，不能连(就是不能自己连接自己)
            result = false;
            break validate;
          }

          if (cellViewT.model.isLink()) {
            // target cell 是线条时，不能连
            result = false;
            break validate;
          }

          if (!magnetT) {
            // target 点没有时，不能连
            result = false;
            break validate;
          }

          // 检查 target 点是否已连接一条先，如果是，不能连接
          const modelT = cellViewT.model;
          result = modelT.validateConnection(
            cellViewS,
            magnetS,
            cellViewT,
            magnetT,
            end,
            linkView
          );
        }

        return result;
      },
      markAvailable: true,
    });

    this.graph = graph;
    this.paper = paper;

    this.paperListener();
    this.graphListener();
  }

  graphListener() {
    // this.graph.on("change:position", (cellView) => {
    //   if (cellView.attributes.type !== "custom.selectArea") {
    //     const links = this.graph.getLinks();
    //     for (let i = 0; i < links.length; i++) {
    //       const link = links[i];
    //       link.findView(this.paper).requestConnectionUpdate();
    //     }
    //   }
    // });

    this.graph.on("remove", (cell) => {
      let selectedElements = R.clone(this.selectedElements);
      if (selectedElements.length === 1) {
        const selectedElement = selectedElements[0];
        if (cell.isLink()) {
          const linkCell = cell;
          const source = linkCell.source();
          const target = linkCell.target();

          if (selectedElement.model.id === source.id) {
            selectedElement.model.activatePropertiesFormListener();
          } else if (selectedElement.model.id === target.id) {
            selectedElement.model.activatePropertiesFormListener();
          }
        } else if (
          cell.isElement() &&
          cell.attributes.type !== "custom.selectArea"
        ) {
          selectedElement.model.activatePropertiesFormListener();
        }
      }

      if (cell.isElement() && cell.attributes.type !== "custom.selectArea") {
        const findIndex = selectedElements.findIndex((element) => {
          return element.model.id === cell.id;
        });

        if (findIndex !== -1) {
          selectedElements = R.remove(findIndex, 1, selectedElements);
          this.selectedElements = selectedElements;

          for (let keyValue of Object.entries(
            this.selectedElementsChangeHook
          )) {
            keyValue[1](selectedElements);
          }

          this.requestAllConnectionUpdate();
        }
      }
    });
  }

  highlightElements(elements) {
    elements.map((element) => {
      joint.highlighters.mask.add(
        element,
        { selector: "root" },
        "stencil-highlight,",
        {
          deep: true,
          attrs: {
            stroke: "rgba(15, 159, 100, 1)",
            strokeWidth: 3,
          },
        }
      );
    });
  }

  removeHighlightElements(elements) {
    elements.map((element) => {
      joint.highlighters.mask.remove(element);
    });
  }

  removeAllHighlightElements() {
    const elements = this.graph.getCells();

    elements.map((element) => {
      joint.dia.HighlighterView.remove(element);
    });
  }

  paperListener() {
    const setSelectedElements = (cellViews) => {
      this.selectedElements = cellViews;
    };

    this.paper.on("link:mouseenter", (cellView) => {
      this.highlightElements([cellView]);
      cellView.addTools(
        new joint.dia.ToolsView({
          name: "onhover",
          tools: [
            new joint.linkTools.Remove({ distance: "80%" }),
            new joint.linkTools.TargetArrowhead(),
          ],
        })
      );
    });

    this.paper.on("link:mouseleave", (cellView) => {
      this.removeHighlightElements([cellView]);
      if (!cellView.hasTools("onhover")) return;
      cellView.removeTools();
    });

    this.paper.on("element:mouseenter", (cellView) => {
      this.highlightElements([cellView]);
      cellView.addTools(
        new joint.dia.ToolsView({
          tools: [
            new joint.elementTools.Remove({
              useModelGeometry: true,
              y: "0%",
              x: "100%",
              offset: { x: 3, y: -3 },
            }),
          ],
        })
      );
    });

    this.paper.on("element:mouseleave", (cellView) => {
      cellView.removeTools();
      if (!this.selectedElements.length) {
        this.removeHighlightElements([cellView]);
      } else {
        const find = this.selectedElements.find(
          (element) => element.id === cellView.id
        );

        if (!find) {
          this.removeHighlightElements([cellView]);
        }
      }
    });

    this.paper.on("element:pointerclick", (cellView, evt) => {
      if (!evt.ctrlKey) {
        this.removeHighlightElements(this.selectedElements);
        setSelectedElements([]);
        setSelectedElements([cellView]);
        this.highlightElements([cellView]);
        for (let keyValue of Object.entries(this.selectedElementsChangeHook)) {
          keyValue[1]([cellView]);
        }
      } else {
        const selectedElements = this.selectedElements;
        const findIndex = selectedElements.findIndex(
          (item) => item.id == cellView.id
        );

        if (findIndex === -1) {
          setSelectedElements([...selectedElements, cellView]);
          this.highlightElements([cellView]);
        } else {
          selectedElements.splice(findIndex, 1);
          this.selectedElements = selectedElements;
          this.removeHighlightElements([cellView]);
        }

        for (let keyValue of Object.entries(this.selectedElementsChangeHook)) {
          keyValue[1](this.selectedElements);
        }
      }
    });

    this.paper.on("element:pointerdblclick", (cellView) => {
      this.removeHighlightElements(this.selectedElements);
      setSelectedElements([cellView]);
      this.highlightElements([cellView]);
      for (let keyValue of Object.entries(this.selectedElementsChangeHook)) {
        keyValue[1]([cellView]);
      }
      const model = cellView.model;
      model.listenerHooker("dblclick", cellView);
    });

    this.paper.on("blank:pointerclick", () => {
      this.removeHighlightElements(
        this.graph.getElements().map((element) => {
          return element.findView(this.paper);
        })
      );
      setSelectedElements([]);
      for (let keyValue of Object.entries(this.selectedElementsChangeHook)) {
        keyValue[1]([]);
      }
    });

    this.paper.on("blank:pointerdown", (event, x, y) => {
      const selectArea = (this.selectArea = {});
      const cell = new SelectArea();

      cell.position(x, y);
      selectArea.x = x;
      selectArea.y = y;
      selectArea.cell = cell;

      this.graph.addCell(cell);
    });

    this.paper.on("blank:pointermove", (event, x, y) => {
      const selectArea = this.selectArea;
      const cell = selectArea.cell;
      const bbox = new joint.g.Rect(
        selectArea.x,
        selectArea.y,
        x - selectArea.x,
        y - selectArea.y
      );
      bbox.normalize();
      cell.set({
        position: { x: bbox.x, y: bbox.y },
        size: {
          width: Math.max(bbox.width, 1),
          height: Math.max(bbox.height, 1),
        },
      });
    });

    this.paper.on("blank:pointerup", () => {
      const cell = this.selectArea.cell;
      const bbox = cell.getBBox();
      try {
        cell.remove();
      } catch (error) {
        // console.log("blank:pointerup", error);
      }
      const elementViews = this.paper.findViewsInArea(bbox);
      setSelectedElements(elementViews);
      this.highlightElements(elementViews);
      for (let keyValue of Object.entries(this.selectedElementsChangeHook)) {
        keyValue[1](elementViews);
      }
    });

    this.paper.on("element:pointermove", (cellView, evt) => {
      if (this.selectedElements.length > 1) {
        const originalEvent = evt.originalEvent;
        const elements = this.selectedElements.filter(
          (element) => element.id !== cellView.id
        );
        elements.map((element) => {
          const position = element.model.position();
          element.model.position(
            position.x + originalEvent.movementX,
            position.y + originalEvent.movementY
          );
        });
      }
    });

    this.paper.on("element:pointerup", (cellView) => {
      if (cellView.attributes.type !== "custom.selectArea") {
        this.requestAllConnectionUpdate();
      }
    });

    this.paper.on("link:connect", (linkView) => {
      if (this.selectedElements.length === 1) {
        const selectedElement = this.selectedElements[0];
        const linkCell = linkView.model;
        const source = linkCell.source();
        const target = linkCell.target();

        if (selectedElement.model.id === source.id) {
          selectedElement.model.activatePropertiesFormListener();
        } else if (selectedElement.model.id === target.id) {
          selectedElement.model.activatePropertiesFormListener();
        }
      }
    });

    this.paper.on("link:disconnect", (linkView) => {
      if (this.selectedElements.length === 1) {
        const selectedElement = this.selectedElements[0];
        const linkCell = linkView.model;
        const source = linkCell.source();
        const target = linkCell.target();

        if (selectedElement.model.id === source.id) {
          selectedElement.model.activatePropertiesFormListener();
        } else if (selectedElement.model.id === target.id) {
          selectedElement.model.activatePropertiesFormListener();
        }
      }
    });
  }

  requestAllConnectionUpdate() {
    const links = this.graph.getLinks();
    for (let i = 0; i < links.length; i++) {
      const link = links[i];
      link.findView(this.paper).requestConnectionUpdate();
    }
  }

  getJson() {
    return this.graph.toJSON();
  }

  addSelectedElementChangeHook(hooker) {
    const { selectedElementsChangeHookCount, selectedElementsChangeHook } =
      this;
    const newHookCount = selectedElementsChangeHookCount + 1;
    this.selectedElementsChangeHook = {
      ...selectedElementsChangeHook,
      [newHookCount]: hooker,
    };

    this.selectedElementsChangeHookCount = newHookCount;

    return newHookCount;
  }

  addEl(position, createCell) {
    let positionWithScale = {
      x: position.x / this.scaleTimes,
      y: position.y / this.scaleTimes,
    };

    const cell = createCell(positionWithScale);
    this.graph.addCell(cell);
    cell.initElementName();
    cell.initStencilElementKey();
    cell.initData();
    this.requestAllConnectionUpdate();

    if (this.selectedElements.length === 1) {
      const selectedElement = this.selectedElements[0];
      selectedElement.model.activatePropertiesFormListener();
    }
  }

  setResult(resultData) {
    const resultDataObj = {};
    resultData.map((item) => {
      resultDataObj[item.resultKey] = item.result;
    });
    const models = this.graph.getElements();
    let noInputModels = models.filter((model) => {
      return Boolean(
        model.getStencilType() !== "input" &&
          model.getStencilType() !== "output"
      );
    });

    noInputModels.map((model) => {
      const stencilElementKey = model.stencilElementKey;
      model.setResult(`执行结果:${resultDataObj[stencilElementKey]}`);
    });
  }

  runRule() {
    try {
      const rule = this.getRule();
      const params = {
        ruleEngineld: 99,
        parameters: rule,
      };
      expressionCalculate.request(params).then((result) => {
        if (result.bizCode === 1) {
          const resultDataObj = {};
          result.data.map((item) => {
            resultDataObj[item.resultKey] = item.result;
          });
          const models = this.graph.getElements();
          let noInputModels = models.filter((model) => {
            return model.getStencilType() !== "input";
          });

          noInputModels.map((model) => {
            const stencilElementKey = model.stencilElementKey;
            model.setResult(`执行结果:${resultDataObj[stencilElementKey]}`);
          });

          message.success("执行成功", 4);
        } else message.warning(result.bizRemind, 4);
      });
    } catch (error) {
      message.warning(error);
    }
  }

  getRule() {
    const models = this.graph.getElements();

    let noOutModels = models.filter((model) => {
      return !model.checkOutPortIsUsed();
    });

    noOutModels = models.filter((model) => {
      return model.checkInPortHasUsed();
    });

    const expressionsObj = {};

    const getExpressionKey = (model) => {
      return model.stencilElementKey;
    };

    const getChildIndex = (parentModel, childModel) => {
      const targetLinks = this.graph.getConnectedLinks(parentModel, {
        outbound: true,
      });
      let result = -1;

      for (let i = 0; i < targetLinks.length; i++) {
        const link = targetLinks[i];
        const target = link.target();

        if (target.id === childModel.id) {
          result = i;
          break;
        }
      }

      return result;
    };

    const getSourceModels = (model) => {
      const sourceLinks = this.graph.getConnectedLinks(model, {
        inbound: true,
      });
      return sourceLinks.map((link) => {
        return link.getSourceCell();
      });
    };

    const checkIsParallel = (model) => {
      if (model.outputType === "number") {
        const targetLinks = this.graph.getConnectedLinks(model, {
          outbound: true,
        });

        return Boolean(targetLinks.length > 1);
      } else return false;
    };

    const checkIsJudgement = (model) => {
      if (model.outputType === "boolean") {
        const targetLinks = this.graph.getConnectedLinks(model, {
          outbound: true,
        });

        return Boolean(targetLinks.length > 1);
      } else return false;
    };

    const analyseSource = (config) => {
      const { isRoot, model, childInfo, isParallel, isJudgement, rootKey } =
        config;
      // const stencilType = model.getStencilType();
      const expressionKey = getExpressionKey(model);
      if (isRoot) {
        if (!expressionsObj[expressionKey]) {
          expressionsObj[expressionKey] = {
            stencilType: model.stencilType,
            children: [],
          };
          if (isParallel) {
            expressionsObj[expressionKey].isParallel = true;
          }

          if (isJudgement) {
            expressionsObj[expressionKey].isJudgement = true;
          }

          if (model.stencilType === "output") {
            expressionsObj[expressionKey].isOutput = true;
            const saveEnumerationId = model.getDataCopy().enumerationId;
            if (!R.isNil(saveEnumerationId)) {
              expressionsObj[expressionKey].saveEnumerationId =
                saveEnumerationId;
            }
          }
        } else {
          if (childInfo) {
            expressionsObj[expressionKey].children[childInfo.index] =
              childInfo.key;
          }
          if (isParallel) {
            expressionsObj[expressionKey].isParallel = true;
          }

          if (isJudgement) {
            expressionsObj[expressionKey].isJudgement = true;
          }

          if (model.stencilType === "output") {
            expressionsObj[expressionKey].isOutput = true;
            const outputdataCopy = model.getDataCopy();
            const elementName = model.attr("elementName/text");
            const {
              outputType,
              enumerationId,
              thingsModalId,
              modelPropertyId,
              modelPropertyDeviceId,
            } = outputdataCopy;
            if (!R.isNil(outputType) && !R.isEmpty(outputType))
              throw `${elementName}: 请选择输出类型`;

            if (outputType === "device") {
              if (R.isNil(thingsModalId)) throw `${elementName}: 请选择物模型`;
              if (R.isNil(modelPropertyId)) throw `${elementName}: 请选择属性`;
              if (
                !R.isNil(modelPropertyDeviceId) &&
                !R.isEmpty(modelPropertyDeviceId)
              )
                throw `${elementName}: 请选择设备`;
              expressionsObj[expressionKey].saveOutputType = outputType;
              expressionsObj[expressionKey].saveModalId = thingsModalId;
              expressionsObj[expressionKey].saveModelPropertyId =
                modelPropertyId;
              expressionsObj[expressionKey].saveDeviceId =
                modelPropertyDeviceId;
            } else if (outputType === "enumeration") {
              if (!R.isNil(enumerationId)) throw `${elementName}: 请选择枚举`;
              expressionsObj[expressionKey].saveOutputType = outputType;
              expressionsObj[expressionKey].saveEnumerationId = enumerationId;
            }
          }

          return;
        }
      }

      if (childInfo) {
        expressionsObj[expressionKey].children[childInfo.index] = childInfo.key;
      }

      const sourceModels = getSourceModels(model);

      let rootInputCount = 0;
      for (let i = 0; i < sourceModels.length; i++) {
        const sourceModel = sourceModels[i];
        const sourceModelStencilType = sourceModel.getStencilType();
        const sourceModelExpressionKey = getExpressionKey(sourceModel);

        if (isRoot) {
          if (!expressionsObj[expressionKey].root) {
            expressionsObj[expressionKey].root = {
              params: [],
              operator: model.operator,
            };
          }
          if (sourceModelStencilType === "input") {
            const dataCopy = sourceModel.getDataCopy();
            const elementName = sourceModel.attr("elementName/text");
            if (dataCopy.inputType === "constant") {
              const constantValue = dataCopy.constantValue;
              if (R.isNil(constantValue)) throw `${elementName}: 请输入常量值`;
              expressionsObj[expressionKey].root.params.push(constantValue);
            } else if (dataCopy.inputType === "device") {
              const thingsModalId = dataCopy.thingsModalId;
              const modelPropertyId = dataCopy.modelPropertyId;
              const modelPropertyDeviceId = dataCopy.modelPropertyDeviceId;
              let tmp = "";
              if (R.isNil(thingsModalId)) throw `${elementName}: 请选择物模型`;
              tmp = `m${thingsModalId}`;
              if (R.isNil(modelPropertyId)) throw `${elementName}: 请选择属性`;
              tmp = `${tmp}p${modelPropertyId}`;
              if (
                !R.isNil(modelPropertyDeviceId) &&
                !R.isEmpty(modelPropertyDeviceId)
              ) {
                tmp = `${tmp}d${modelPropertyDeviceId}`;
              }
              expressionsObj[expressionKey].root.params.push(tmp);
            } else if (dataCopy.inputType === "enumeration") {
              const enumerationId = dataCopy.enumerationId;
              if (!R.isNil(enumerationId) && !R.isEmpty(enumerationId)) {
                expressionsObj[expressionKey].root.params.push(
                  `e${enumerationId}`
                );
              } else {
                throw `${elementName}: 请选择枚举`;
              }
            } else {
              throw `${elementName}: 请选择输入类型`;
            }
            rootInputCount += 1;
          } else {
            expressionsObj[expressionKey].root.params.push(
              sourceModelExpressionKey
            );
          }
        } else {
          if (!expressionsObj[rootKey][expressionKey]) {
            expressionsObj[rootKey][expressionKey] = {
              params: [],
              operator: model.operator,
            };
          }
          if (sourceModelStencilType === "input") {
            const dataCopy = sourceModel.getDataCopy();
            const elementName = sourceModel.attr("elementName/text");
            if (dataCopy.inputType === "constant") {
              const constantValue = dataCopy.constantValue;
              if (R.isNil(constantValue)) throw `${elementName}: 请输入常量值`;
              expressionsObj[expressionKey].root.params.push(constantValue);
            } else if (dataCopy.inputType === "device") {
              const thingsModalId = dataCopy.thingsModalId;
              const modelPropertyId = dataCopy.modelPropertyId;
              const modelPropertyDeviceId = dataCopy.modelPropertyDeviceId;
              let tmp = "";
              if (R.isNil(thingsModalId)) throw `${elementName}: 请选择物模型`;
              tmp = `m${thingsModalId}`;
              if (R.isNil(modelPropertyId)) throw `${elementName}: 请选择属性`;
              tmp = `${tmp}p${modelPropertyId}`;
              if (
                !R.isNil(modelPropertyDeviceId) &&
                !R.isEmpty(modelPropertyDeviceId)
              ) {
                tmp = `${tmp}d${modelPropertyDeviceId}`;
              }
              expressionsObj[expressionKey].root.params.push(tmp);
            } else if (dataCopy.inputType === "enumeration") {
              const enumerationId = dataCopy.enumerationId;
              if (!R.isNil(enumerationId) && !R.isEmpty(enumerationId)) {
                expressionsObj[expressionKey].root.params.push(
                  `e${enumerationId}`
                );
              } else {
                throw `${elementName}: 请选择枚举`;
              }
            } else {
              throw `${elementName}: 请选择输入类型`;
            }
          } else {
            expressionsObj[rootKey][expressionKey].params.push(
              sourceModelExpressionKey
            );
          }
        }
      }
      if (isRoot && rootInputCount === sourceModels.length) {
        expressionsObj[expressionKey].isTop = true;
      }

      for (let i = 0; i < sourceModels.length; i++) {
        const sourceModel = sourceModels[i];
        const sourceModelStencilType = sourceModel.getStencilType();

        if (checkIsParallel(sourceModel)) {
          analyseSource({
            model: sourceModel,
            isRoot: true,
            isParallel: true,
            childInfo: {
              key: expressionKey,
              index: getChildIndex(sourceModel, model),
            },
          });
        } else if (checkIsJudgement(sourceModel)) {
          analyseSource({
            model: sourceModel,
            isRoot: true,
            isJudgement: true,
            childInfo: {
              key: expressionKey,
              index: getChildIndex(sourceModel, model),
            },
          });
        } else if (sourceModelStencilType === "output") {
          analyseSource({
            model: sourceModel,
            isRoot: true,
            isParallel: true,
            isOutput: true,
            childInfo: {
              key: expressionKey,
              index: getChildIndex(sourceModel, model),
            },
          });
        } else {
          if (sourceModelStencilType !== "input")
            analyseSource({
              model: sourceModel,
              isRoot: true,
              isParallel: true,
              childInfo: {
                key: expressionKey,
                index: getChildIndex(sourceModel, model),
              },
            });
        }
      }
    };

    for (let i = 0; i < noOutModels.length; i++) {
      const noOutModel = noOutModels[i];
      const stencilType = noOutModel.getStencilType();
      if (stencilType !== "input") {
        analyseSource({ model: noOutModel, isRoot: true });
      }
    }

    const getExpression = (key, expressionObj, expressionKey) => {
      const alphabetRegex = new RegExp("^[a-zA-Z]");
      const deviceModelRegex = /^m(?:\d{1,})p(?:\d{1,})/;
      const enumerationRegex = /^e(?:\d{1,})/;
      const outputRegex = /^output(?:\d{1,})/;
      const params = [];

      for (let i = 0; i < expressionObj[key].params.length; i++) {
        const param = expressionObj[key].params[i];

        if (alphabetRegex.test(param)) {
          if (!deviceModelRegex.test(param) && !enumerationRegex.test(param)) {
            if (outputRegex.test(param)) {
              if (!expressionsObj[expressionKey].referResultKeyList)
                expressionsObj[expressionKey].referResultKeyList = [];
              expressionsObj[expressionKey].referResultKeyList.push(
                expressionsObj[param].referResultKeyList[0]
              );
              params.push(expressionsObj[param].referResultKeyList[0]);
            } else {
              if (!expressionsObj[expressionKey].referResultKeyList)
                expressionsObj[expressionKey].referResultKeyList = [];
              expressionsObj[expressionKey].referResultKeyList.push(param);
              params.push(param);
            }
          } else {
            params.push(param);
          }
        } else {
          params.push(param);
        }
      }

      return params.join(expressionObj[key].operator);
    };

    for (let [expressionKey, expressionObj] of Object.entries(expressionsObj)) {
      if (expressionObj.stencilType === "output") {
        expressionsObj[expressionKey].referResultKeyList = [
          expressionObj.root.params[0],
        ];
      }
    }

    for (let [expressionKey, expressionObj] of Object.entries(expressionsObj)) {
      if (expressionObj.stencilType !== "output") {
        const expressionStr = getExpression(
          "root",
          expressionObj,
          expressionKey
        );
        expressionsObj[expressionKey].expression = expressionStr;
      }
    }

    console.log("expressionsObj", expressionsObj);

    const result = [];

    const genResult = (expressionObj, expressionKey) => {
      expressionsObj[expressionKey].isFinish = true;
      const tmp = {
        expression: expressionObj.expression,
        conditionFlag: (() => {
          if (expressionObj.isParallel) return 2;
          else if (expressionObj.isJudgement) return 1;
          else return 0;
        })(),
        operator: expressionsObj[expressionKey].stencilType,
        saveResult: {
          resultKey: expressionKey,
          resultType: (() => {
            if (expressionObj.isParallel) return "init";
            else if (expressionObj.isJudgement) return "boolean";
            else return "init";
          })(),
          persistenceFlag: true,
        },
      };

      if (expressionObj.referResultKeyList) {
        tmp.referResult = true;
        tmp.referResultKeyList1 = expressionObj.referResultKeyList;
      }

      if (expressionObj.isParallel) {
        tmp.parallelConditionParameter = [];
        for (let i = 0; i < expressionObj.children.length; i++) {
          const expressionChildKey = expressionObj.children[i];
          if (expressionsObj[expressionChildKey].stencilType === "output") {
            if (
              expressionsObj[expressionChildKey].saveOutputType ===
              "enumeration"
            ) {
              tmp.saveResult.enumId =
                expressionsObj[expressionChildKey].saveEnumerationId;
            } else if (
              expressionsObj[expressionChildKey].saveOutputType === "device"
            ) {
              tmp.saveResult.modalId =
                expressionsObj[expressionChildKey].saveModalId;
              tmp.saveResult.modelPropertyId =
                expressionsObj[expressionChildKey].saveModelPropertyId;
              tmp.saveResult.deviceId =
                expressionsObj[expressionChildKey].deviceId;
            }

            if (!expressionsObj[expressionChildKey].isFinish) {
              expressionsObj[expressionChildKey].isFinish = true;

              for (
                let j = 0;
                j < expressionsObj[expressionChildKey].children.length;
                j++
              ) {
                const outputExpressionChildKey =
                  expressionsObj[expressionChildKey].children[j];
                if (!expressionsObj[outputExpressionChildKey].isFinish) {
                  const tmpChild = genResult(
                    expressionsObj[outputExpressionChildKey],
                    outputExpressionChildKey
                  );
                  tmp.parallelConditionParameter.push({
                    sort: tmp.parallelConditionParameter.length + 1,
                    ...tmpChild,
                  });
                }
              }
            }
          } else {
            if (!expressionsObj[expressionChildKey].isFinish) {
              const tmpChild = genResult(
                expressionsObj[expressionChildKey],
                expressionChildKey
              );
              tmp.parallelConditionParameter.push({
                sort: tmp.parallelConditionParameter.length + 1,
                ...tmpChild,
              });
            }
          }
        }
        if (!tmp.parallelConditionParameter.length) {
          delete tmp.parallelConditionParameter;
          tmp.conditionFlag = 0;
        }
      } else if (expressionObj.isJudgement) {
        tmp.fullConditionParameter = [];
        for (let i = 0; i < expressionObj.children.length; i++) {
          const expressionChildKey = expressionObj.children[i];
          if (expressionsObj[expressionChildKey].stencilType === "output") {
            if (
              expressionsObj[expressionChildKey].saveOutputType ===
              "enumeration"
            ) {
              tmp.saveResult.enumId =
                expressionsObj[expressionChildKey].saveEnumerationId;
            } else if (
              expressionsObj[expressionChildKey].saveOutputType === "device"
            ) {
              tmp.saveResult.modalId =
                expressionsObj[expressionChildKey].saveModalId;
              tmp.saveResult.modelPropertyId =
                expressionsObj[expressionChildKey].saveModelPropertyId;
              tmp.saveResult.deviceId =
                expressionsObj[expressionChildKey].deviceId;
            }

            if (!expressionsObj[expressionChildKey].isFinish) {
              expressionsObj[expressionChildKey].isFinish = true;

              for (
                let j = 0;
                j < expressionsObj[expressionChildKey].children.length;
                j++
              ) {
                const outputExpressionChildKey =
                  expressionsObj[expressionChildKey].children[j];
                if (!expressionsObj[outputExpressionChildKey].isFinish) {
                  const tmpChild = genResult(
                    expressionsObj[outputExpressionChildKey],
                    outputExpressionChildKey
                  );
                  tmp.fullConditionParameter.push({
                    sort: tmp.fullConditionParameter.length + 1,
                    ...tmpChild,
                  });
                }
              }
            }
          } else {
            if (!expressionsObj[expressionChildKey].isFinish) {
              const tmpChild = genResult(
                expressionsObj[expressionChildKey],
                expressionChildKey
              );
              tmp.fullConditionParameter.push({
                sort: tmp.fullConditionParameter.length + 1,
                ...tmpChild,
              });
            }
          }
        }
        if (!tmp.fullConditionParameter.length) {
          delete tmp.fullConditionParameter;
          tmp.conditionFlag = 0;
        }
      }

      return tmp;
    };

    for (let [expressionKey, expressionObj] of Object.entries(expressionsObj)) {
      if (expressionObj.isTop && !expressionObj.isFinish) {
        const tmp = genResult(expressionObj, expressionKey);
        result.push({
          sort: result.length + 1,
          ...tmp,
        });
      }
    }

    return result;
  }

  selectedElementAlignLeft() {
    const selectedElements = this.selectedElements;

    const minX = R.apply(
      Math.min,
      selectedElements.map((element) => {
        const position = element.model.position();
        return position.x;
      })
    );

    selectedElements.map((element) => {
      const position = element.model.position();
      element.model.position(minX, position.y);
    });

    this.requestAllConnectionUpdate();
  }

  selectedElementAlignRight() {
    const selectedElements = this.selectedElements;
    const maxX = R.apply(
      Math.max,
      selectedElements.map((element) => element.model.position().x)
    );
    selectedElements.map((element) => {
      const position = element.model.position();
      element.model.position(maxX, position.y);
    });

    this.requestAllConnectionUpdate();
  }

  selectedElementAlignCenter() {
    const selectedElements = this.selectedElements;
    const maxX = R.apply(
      Math.max,
      selectedElements.map((element) => element.model.position().x)
    );
    const minX = R.apply(
      Math.min,
      selectedElements.map((element) => element.model.position().x)
    );
    const median = (maxX + minX) / 2;

    selectedElements.map((element) => {
      const position = element.model.position();
      element.model.position(median, position.y);
    });

    this.requestAllConnectionUpdate();
  }

  deleteAllSelectedElement() {
    const selectedElements = this.selectedElements;

    selectedElements.map((element) => {
      element.model.remove();
    });

    this.selectedElements = [];
    for (let keyValue of Object.entries(this.selectedElementsChangeHook)) {
      keyValue[1]([]);
    }

    this.requestAllConnectionUpdate();
  }

  deleteElement(id) {
    let selectedElements = this.selectedElements;
    const cell = this.graph.getCell(id);

    const findIndex = selectedElements.findIndex((element) => {
      return element.model.id === id;
    });

    if (findIndex !== -1) {
      selectedElements = R.remove(findIndex, 1, selectedElements);
      this.selectedElements = selectedElements;

      for (let keyValue of Object.entries(this.selectedElementsChangeHook)) {
        keyValue[1](selectedElements);
      }

      this.requestAllConnectionUpdate();
    }

    cell.remove();
  }

  scale(value) {
    this.scaleTimes = value;
    this.paper.scale(value);
  }

  readJson(json) {
    this.graph.removeCells();
    let elementsJsonObj = {};
    let linksJsonObj = {};
    let oldElementsRelateObj = {};

    const elementRegExp = new RegExp("^stencil.");
    const linkRegExp = new RegExp("custom.defalutLink");

    json.cells.map((cellItem) => {
      if (elementRegExp.test(cellItem.type)) {
        elementsJsonObj[cellItem.id] = cellItem;
      } else if (linkRegExp.test(cellItem.type)) {
        linksJsonObj[cellItem.id] = cellItem;
      }
    });

    for (let [cellId, elementJson] of Object.entries(elementsJsonObj)) {
      const type = R.replace(elementRegExp, "", elementJson.type);
      const stencil = stencilsObj[type];
      const cell = stencil.reloadCell(elementJson);
      this.graph.addCell(cell);
      cell.initData();

      const oldPorts = (() => {
        let oldInPorts = [];
        let oldOutPorts = [];

        if (
          elementJson.ports &&
          Array.isArray(elementJson.ports.items) &&
          elementJson.ports.items.length
        ) {
          elementJson.ports.items.map((item) => {
            if (item.group === "in") oldInPorts.push(item);
            else if (item.group === "out") oldOutPorts.push(item);
          });
        }

        return {
          oldInPorts,
          oldOutPorts,
        };
      })();
      const newInPorts = cell.getGroupPorts("in");
      const newOutPorts = cell.getGroupPorts("out");

      const tmp = {
        oldCellId: cellId,
        newCellId: cell.id,
        inPorts: {},
        outPorts: {},
      };

      for (let i = 0; i < oldPorts.oldInPorts.length; i++) {
        const oldInPort = oldPorts.oldInPorts[i];
        tmp.inPorts[oldInPort.id] = { newId: newInPorts[i].id };
      }

      for (let i = 0; i < oldPorts.oldOutPorts.length; i++) {
        const oldOutPort = oldPorts.oldOutPorts[i];
        tmp.outPorts[oldOutPort.id] = { newId: newOutPorts[i].id };
      }
      oldElementsRelateObj[cellId] = tmp;
    }

    for (let linkIdAndlinkJson of Object.entries(linksJsonObj)) {
      const linkJson = linkIdAndlinkJson[1];
      const link = new DefalutLink();
      const sourceJson = linkJson.source;
      const targetJson = linkJson.target;

      link.source({
        id: oldElementsRelateObj[sourceJson.id].newCellId,
        port: oldElementsRelateObj[sourceJson.id].outPorts[sourceJson.port]
          .newId,
      });

      link.target({
        id: oldElementsRelateObj[targetJson.id].newCellId,
        port: oldElementsRelateObj[targetJson.id].inPorts[targetJson.port]
          .newId,
      });
      this.graph.addCell(link);
    }

    this.requestAllConnectionUpdate();
  }
}

export default {
  install: (Vue) => {
    const joint = new Joint();
    Vue.config.globalProperties.$joint = joint;
    Vue.provide("joint", joint);
  },
};
